import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

export default function LinkOffspringDialog({ open, onOpenChange, cattle, allCattle, onLink }) {
  const [selectedOffspring, setSelectedOffspring] = useState('');

  // Filter potential offspring: younger animals without this parent assigned
  const potentialOffspring = allCattle.filter(c => {
    // Don't include self
    if (c.id === cattle.id) return false;
    
    // Calculate age difference
    if (!c.birthDate || !cattle.birthDate) return true; // Include if dates are missing
    
    const cattleDate = new Date(cattle.birthDate);
    const calfDate = new Date(c.birthDate);
    
    // Calf must be born after parent
    if (calfDate <= cattleDate) return false;
    
    // If this is a female (mother), check if not already assigned
    if (cattle.gender === 'Hembra') {
      return c.motherId !== cattle.id;
    }
    
    // If this is a male (father), check if not already assigned
    if (cattle.gender === 'Macho') {
      return c.fatherId !== cattle.id;
    }
    
    return true;
  }).sort((a, b) => {
    // Sort by birth date, newest first
    if (!a.birthDate) return 1;
    if (!b.birthDate) return -1;
    return new Date(b.birthDate) - new Date(a.birthDate);
  });

  const handleLink = async () => {
    if (!selectedOffspring) {
      toast.error('Debe seleccionar una cría');
      return;
    }

    await onLink(selectedOffspring);
    setSelectedOffspring('');
    onOpenChange(false);
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return 'Edad desconocida';
    const birth = new Date(birthDate);
    const today = new Date();
    const months = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    return `${years}a ${remainingMonths}m`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Vincular Cría a {cattle?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Seleccionar Cría</Label>
            <Select value={selectedOffspring} onValueChange={setSelectedOffspring}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar animal..." />
              </SelectTrigger>
              <SelectContent>
                {potentialOffspring.length > 0 ? (
                  potentialOffspring.map(animal => (
                    <SelectItem key={animal.id} value={animal.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{animal.name}</span>
                        <span className="text-muted-foreground">({animal.tag})</span>
                        <span className="text-xs text-muted-foreground">
                          - {calculateAge(animal.birthDate)}
                        </span>
                      </div>
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="none" disabled>
                    No hay animales disponibles para vincular
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Solo se muestran animales más jóvenes que {cattle?.name}
            </p>
          </div>

          {selectedOffspring && (
            <div className="bg-rose-50 p-3 rounded-lg">
              <p className="text-sm text-rose-900">
                Se vinculará este animal como {cattle?.gender === 'Hembra' ? 'cría de esta madre' : 'cría de este padre'}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleLink}
            className="bg-rose-600 hover:bg-rose-700"
            disabled={!selectedOffspring}
          >
            Vincular Cría
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
